import { CurrencyEnum } from '__generated__/types';
import { IMoney } from '@xometry/money';
import { FormInstance } from 'antd';
import { AxiosError } from 'axios';
import { IncomingInvoiceFormValues } from 'components/Deal/IncomingInvoice/Form/shared/models';
import Notification from 'components/UI/Notification';
import { dateFormatToBackend } from 'components/utils/formatters';
import { CURRENT_REGION } from 'config/regions/config';
import {
  II_POSITIONS_NEED_CURRENCY,
  II_ZERO_VAT_RATE_ENABLED,
  PROVIDER_NOT_VAT_PAYER_ENABLED,
  XOMETRY_VAT_ENABLED,
} from 'config/regions/features';
import { Regions } from 'config/regions/types';
import { getRegionCurrency } from 'config/regions/utils';
import { groupBy, last, map, omit, size, startsWith, uniq } from 'es-toolkit/compat';
import {
  ICachedXometryVatNumber,
  IncomingInvoiceState,
  PartData,
  Position,
  PositionII,
  PreloadedFile,
} from 'interfaces';
import { OIShippingDetails } from 'interfaces/graphql';
import { IncomingInvoiceValidityEnum } from 'interfaces/graphql/incomingInvoice';
import { action, computed, observable } from 'mobx';
import React from 'react';
import { routes } from 'routes';
import { routesApi } from 'routes/api';
import type { RootStore } from 'stores/RootStore';
import omsApi, { camelizeKeys, decamelizeKeys } from 'utils/axios';
import { parseDate } from 'utils/date';
import dayjs, { Dayjs } from 'utils/dayjs';
import { determineAccountingCategory } from 'utils/deal/incomingInvoiceCategorization';
import { getInvoiceFullName, getInvoiceShortName } from 'utils/deal/invoiceName';
import type { DealIncomingInvoiceQuery } from 'utils/graphql/queries/__generated__/dealIncomingInvoice';
import { XMoney } from 'utils/money';
import { uniqID } from 'utils/uniqID';

const DEFAULT_VAT = 0;
const DEFAULT_STRING = '';

export type DealIncomingInvoice = DealIncomingInvoiceQuery['dealIncomingInvoice'];
export type DealIncomingInvoiceWithPreloadedFiles = Omit<DealIncomingInvoice, 'attachedFiles'> & {
  attachedFiles: PreloadedFile[];
};

type InvoicePositionRef = NonNullable<DealIncomingInvoice['invoicePositions']>[number]['ref'];
type LotRef = Extract<InvoicePositionRef, { __typename?: 'Lot' }>;
type LotPart = NonNullable<LotRef['part']>;

export interface UploadedFile extends File {
  uid: number;
}

export interface PositionsByKey {
  [key: string]: PositionII;
}

interface IPrepareFormDataArgs {
  attachedFiles: PreloadedFile[];
  dealId: number;
  positions: PositionsByKey;
  publish: boolean;
  uploads: UploadedFile[];
  values: IncomingInvoiceFormValues;
  invoiceState?: IncomingInvoiceState;
}

interface UpdateAttributesArgs {
  dealId: number;
  invoice?: DealIncomingInvoiceWithPreloadedFiles;
  positions: PositionsByKey;
  values: IncomingInvoiceFormValues;
}

type AxiosResponseError = AxiosError<{ errors?: { source: Record<string, string> } }>;

export interface Provider {
  value: number;
  label: string;
  providerId: number;
  providerAcceptSelfBilling: boolean;
  paymentDueDate: string;
  paymentTerms?: number;
  xometryVatNumber?: ICachedXometryVatNumber;
}

interface IVatInfo {
  sachkontoNumber?: number | null;
  vatCode?: number | null;
  vatRate?: number | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const normalizeProviderContact = (value?: object | null) => {
  const contact = isRecord(value) ? camelizeKeys(value) : {};

  return {
    id: contact.id != null ? Number(contact.id) : undefined,
    name: (contact.name as string | null | undefined) ?? null,
    email: (contact.email as string | null | undefined) ?? null,
    phone: (contact.phone as string | null | undefined) ?? null,
  };
};

const normalizeProviderBankDetails = (value?: object | null) => {
  const details = isRecord(value) ? camelizeKeys(value) : {};

  return {
    bankAccountHolderName: (details.bankAccountHolderName as string | null | undefined) ?? null,
    bankCountry: (details.bankCountry as string | null | undefined) ?? null,
    iban: (details.iban as string | null | undefined) ?? null,
    swiftbic: (details.swiftbic as string | null | undefined) ?? null,
    name: (details.name as string | null | undefined) ?? null,
  };
};

const normalizeProviderBillingAddress = (value?: object | null) => {
  const address = isRecord(value) ? camelizeKeys(value) : {};

  return {
    address: (address.address as string | null | undefined) ?? null,
    city: (address.city as string | null | undefined) ?? null,
    zip: (address.zip as string | null | undefined) ?? null,
    country: (address.country as string | null | undefined) ?? null,
  };
};

const normalizeXometryVatNumberForForm = (value?: DealIncomingInvoice['xometryVatNumber'] | null) => {
  if (!value) {
    return null;
  }

  return {
    id: value.id,
    country: value.country,
    number: value.number,
    validation: value.validation ?? null,
    reverseCharge: value.reverseCharge ?? null,
    reverseChargeRemark: value.reverseChargeRemark ?? null,
    isActive: value.isActive ?? null,
  } as ICachedXometryVatNumber;
};

const normalizeAttachedFiles = (files: DealIncomingInvoice['attachedFiles']) =>
  files.map((file) => ({
    id: Number(file.id),
    name: file.name,
  })) as PreloadedFile[];

const mapPartData = (part: LotPart): PartData => {
  const preQuotedByLabel = part.preQuotedBy?.id
    ? `PR-${part.preQuotedBy.id}${part.preQuotedBy.name ? ` - ${part.preQuotedBy.name}` : ''}`
    : undefined;

  return {
    id: part.id,
    name: part.name ?? DEFAULT_STRING,
    material: part.material ?? undefined,
    postProcessing: part.postProcessing ?? undefined,
    customerNote: part.customerNote ?? undefined,
    productionRemark: part.productionRemark ?? undefined,
    internalComment: part.internalComment ?? undefined,
    isExpress: Boolean(part.isExpress),
    isPreQuote: Boolean(part.preQuoted),
    isSamplesNeeded: Boolean(part.samplesNeeded),
    mpNeeded: part.measurementProtocolNeeded as PartData['mpNeeded'],
    preQuotedByLabel,
    preQuotedComment: part.preQuotedComment ?? undefined,
    samplesQuantity: part.samplesQuantity ?? undefined,
    samplesComment: part.samplesComment ?? undefined,
    hsCode: part.hsCode ?? undefined,
    hsCodeName: part.hsCodeName ?? undefined,
    hsCodeConfirmedAt: null,
  };
};

const prepareFormData = ({
  attachedFiles,
  dealId,
  positions,
  publish,
  uploads,
  values,
  invoiceState,
}: IPrepareFormDataArgs) => {
  const invoicePositionsAttributes = map(Object.values(positions), (position) => ({
    accounting_category: position.categoryName,
    name: position.name,
    position: position.position,
    quantity: position.quantity,
    refId: position.refId,
    refType: position.refType,
    price_per_piece: position.price,
    vat_rate: position.vat,
    id: position.id,
    _destroy: position._destroy,
  }));

  const attachedFilesAttributes = map(attachedFiles, (file) => ({
    id: file.id,
    _destroy: file._destroy,
  }));

  const fieldsToOmit = ['positions'];

  if ((invoiceState === IncomingInvoiceState.Draft && !publish) || publish) {
    fieldsToOmit.push('registeredAt');
  }

  // Do not send the issued_at field when saving changes on a published invoice
  if (invoiceState === IncomingInvoiceState.Published && !publish) {
    fieldsToOmit.push('issuedAt');
  }

  const data: Record<string, unknown> = {
    ...omit(values, fieldsToOmit),
    invoicePositionsAttributes,
    attachedFilesAttributes,
  };

  if (data.provider) {
    const provider = data.provider as Record<string, unknown>;

    if (PROVIDER_NOT_VAT_PAYER_ENABLED) {
      if (provider.notVatPayer === undefined) {
        data.provider = { ...provider, notVatPayer: false };
      }
    } else {
      data.provider = omit(provider, 'notVatPayer');
    }
  }

  // Do not send the registered_at field when saving changes on a draft II's page
  const dateFields = ['holdUntil', 'paidAt', 'paymentDueDate'];

  // Only include issuedAt in dateFields if it's not being omitted
  if (!fieldsToOmit.includes('issuedAt')) {
    dateFields.push('issuedAt');
  }

  if (!fieldsToOmit.includes('registeredAt') && (!invoiceState || invoiceState !== IncomingInvoiceState.Draft)) {
    dateFields.push('registeredAt');
  }

  dateFields.forEach((valueKey) => {
    if (data[valueKey]) {
      data[valueKey] = dateFormatToBackend(data[valueKey] as Dayjs);
    } else {
      data[valueKey] = null;
    }
  });

  const decamelizedData = decamelizeKeys(data);

  const formData = new FormData();

  uploads.forEach((file: UploadedFile) => {
    formData.append('invoice[uploads[][file]]', file);
  });

  formData.append('invoice[deal_id]', String(dealId));

  if (publish) {
    formData.append('invoice[state]', JSON.stringify('published'));
  }

  Object.entries(decamelizedData).forEach(([key, value]) => {
    if (value !== undefined) {
      formData.append(`invoice[${key}]`, JSON.stringify(value));
    } else {
      formData.append(`invoice[${key}]`, 'null');
    }
  });

  return formData;
};

export const getUpdateAttributes = ({ dealId, invoice, positions, values }: UpdateAttributesArgs) => ({
  ...omit(values, 'positions'),
  attachedFilesAttributes: invoice
    ? invoice.attachedFiles.map((file) => ({
        _destroy: file._destroy,
        id: String(file.id),
      }))
    : [],
  dealId,
  holdUntil: values.holdUntil ? dateFormatToBackend(values.holdUntil) : null,
  invoicePositionsAttributes: Object.values(positions).map((position) => ({
    accountingCategory: position.categoryName,
    destroy: position._destroy,
    id: position.id,
    name: position.name,
    position: position.position,
    pricePerPiece: Number(position.price),
    quantity: position.quantity,
    refId: position.refId,
    refType: position.refType,
    vatRate: Number(position.vat),
  })),
  issuedAt: values.issuedAt ? dateFormatToBackend(values.issuedAt) : null,
  paidAt: values.paidAt ? dateFormatToBackend(values.paidAt) : null,
  paymentDueDate: values.paymentDueDate ? dateFormatToBackend(values.paymentDueDate) : null,
  provider: values.provider
    ? {
        ...values.provider,
        id: Number(values.provider.id),
        intacctId: values.provider.intacctId ?? '',
        legalName: values.provider.legalName ?? '',
        payoutTransferType: values.provider.payoutTransferType ?? '',
        vatId: values.provider.vatId ?? '',
      }
    : undefined,
  providerContact: {
    ...values.providerContact,
    id: String(values.providerContact.id),
  },
  registeredAt: values.registeredAt ? dateFormatToBackend(values.registeredAt) : undefined,
});

class IncomingInvoicesStore {
  public rootStore: RootStore;

  form?: FormInstance = undefined;
  currentPosition = 0;

  @observable invoice?: DealIncomingInvoiceWithPreloadedFiles = undefined;
  @observable refInvoiceCurrency: CurrencyEnum = getRegionCurrency();
  @observable uploads: UploadedFile[] = [];
  @observable positions: PositionsByKey = {};
  @observable providers: Provider[] = [];
  @observable selectedProvider?: Provider;
  @observable sourceShipping?: OIShippingDetails;
  @observable vatInfo?: IVatInfo;
  @observable isCreditNote = false;

  @observable private newXometryCreditNote = false;
  @observable private newXometryCreditNoteCorrection = false;
  @observable private providerAcceptSelfBilling?: boolean;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  incCurrentPosition = (inc = 10): number => {
    this.currentPosition += inc;

    return this.currentPosition;
  };

  @action setIsCreditNote = (isCreditNote: boolean) => {
    this.isCreditNote = isCreditNote;
  };

  @action setVatInfo = (vatInfo: IVatInfo) => {
    this.vatInfo = vatInfo;
  };

  @action applyProviderVatRate = (vatRate: number) => {
    // SG keeps 0% for new positions and the persisted rate for restored ones,
    // so a provider-based rate must not rewrite them.
    if (II_ZERO_VAT_RATE_ENABLED) return;

    this.setVatInfo({ vatRate });

    Object.keys(this.positions).forEach((key) => {
      if (!this.positions[key]._destroy) {
        this.positions[key] = { ...this.positions[key], vat: vatRate };
      }
    });

    if (this.form) {
      const positionsFormValues = Object.fromEntries(
        Object.entries(this.positions).map(([key, position]) => [key, { ...position }]),
      );

      this.form.setFieldsValue({ positions: positionsFormValues });
    }
  };

  @action setProviders = (providers: Provider[]) => {
    this.providers = providers;
  };

  @action setSelectedProvider = (selectedProvider: Provider) => {
    this.selectedProvider = selectedProvider;
  };

  @action setCurrentPosition = (position: number): void => {
    this.currentPosition = position;
  };

  @action setSourceShipping = (shipping: OIShippingDetails) => {
    this.sourceShipping = shipping;
  };

  @action setInvoice = (invoice: DealIncomingInvoice) => {
    this.positions = {};

    this.invoice = {
      ...invoice,
      attachedFiles: normalizeAttachedFiles(invoice.attachedFiles),
    };

    this.setVatInfo({
      sachkontoNumber: invoice.sachkontoNumber ? Number(invoice.sachkontoNumber) : undefined,
      vatCode: invoice.datevVatCode ? Number(invoice.datevVatCode) : undefined,
      vatRate: invoice.vatRate ?? undefined,
    });

    (invoice.invoicePositions ?? []).forEach((pos) => {
      const ref = pos.ref;

      const refSource =
        ref && ref.__typename === 'Lot' ? (ref.shipping?.number ?? ref.providerOrder?.number ?? null) : null;

      const part = ref && ref.__typename === 'Lot' && ref.part ? mapPartData(ref.part) : undefined;

      const isMoldPosition = ref?.__typename === 'Lot' && ref.part?.partType === 'mold';

      const categoryName = isMoldPosition
        ? 'im_tools'
        : determineAccountingCategory({
            baseCategory: pos.accountingCategory,
            refSource,
            refType: pos.refType,
            part: ref?.__typename === 'Lot' ? ref.part : undefined,
          });

      const newPosition = {
        categoryName,
        id: pos.id,
        name: pos.name,
        quantity: pos.quantity,
        price: Number(pos.pricePerPieceMoney?.amount ?? 0),
        priceMoney: pos.pricePerPieceMoney,
        part,
        vat: pos.vatRate,
        position: pos.position,
        partId: part?.id,
        refType: pos.refType ?? undefined,
        refSource: refSource && startsWith(String(refSource), 'SH') ? refSource : null,
        refId: pos.refId != null ? Number(pos.refId) : undefined,
      };

      this.addPosition(newPosition, { isRestored: true });
    });

    this.currentPosition = last(Object.values(this.positions))?.position || this.currentPosition;
  };

  @computed({ keepAlive: true })
  get positionsByShippingArray(): PositionII[] {
    return Object.keys(this.positionsByShipping).reduce((acc: PositionII[], cur) => {
      return [...acc, ...this.positionsByShipping[cur]];
    }, []);
  }

  // isRestored marks positions loaded from a saved invoice: in SG, new positions
  // get 0% while restored ones keep their own persisted rate untouched by vatInfo
  // (the invoice-level rate used elsewhere to apply one uniform rate on load).
  private resolvePositionVat = (positionVat?: number, isRestored = false): number => {
    if (II_ZERO_VAT_RATE_ENABLED) {
      return isRestored ? (positionVat ?? DEFAULT_VAT) : DEFAULT_VAT;
    }

    return this.vatInfo?.vatRate != null ? this.vatInfo.vatRate : positionVat || DEFAULT_VAT;
  };

  @action addPositions = (positions: PositionII[], options?: { isRestored?: boolean }) => {
    // cast old positions currency
    if (II_POSITIONS_NEED_CURRENCY) {
      Object.values(this.positions).forEach((position) => {
        this.positions[position.key].priceMoney = {
          ...this.positions[position.key].priceMoney,
          currencyCode: positions[0].priceMoney?.currencyCode,
        } as IMoney;
      });
    }

    Object.values(positions).forEach((position) => {
      const refSource = startsWith(String(position.refSource), 'SH') ? position.refSource : null;

      this.positions[position.key] = {
        ...position,
        categoryName: determineAccountingCategory({
          baseCategory: position.categoryName,
          refSource: position.refSource,
          refType: position.refType,
        }),
        vat: this.resolvePositionVat(position.vat, options?.isRestored),
        position: position.position || this.incCurrentPosition(),
        refSource,
      };
    });
  };

  @action setPositions = (positions: PositionII[], options?: { isRestored?: boolean }) => {
    this.positions = {};
    this.addPositions(positions, options);
  };

  @action addPosition = (
    pos: Partial<PositionII> & Pick<PositionII, 'categoryName' | 'name'>,
    options?: { isRestored?: boolean },
  ) => {
    const key = uniqID();
    const firstPositionCurrency = this.positionsArray[0]?.priceMoney?.currencyCode;
    const currencyCode = firstPositionCurrency || this.refInvoiceCurrency || getRegionCurrency();

    const newPosition = {
      categoryName: determineAccountingCategory({
        baseCategory: pos.categoryName,
        refSource: pos.refSource,
        refType: pos.refType,
      }),
      id: pos.id,
      name: pos.name || '',
      quantity: pos.quantity || 1,
      price: pos.price || 0,
      priceMoney: pos.priceMoney || XMoney(0, currencyCode).toIMoney(),
      vat: this.resolvePositionVat(pos.vat, options?.isRestored),
      position: pos.position || this.incCurrentPosition(),
      part: pos.part,
      partId: pos.partId,
      refType: pos.refType,
      refSource: startsWith(String(pos.refSource), 'SH') ? pos.refSource : null,
      refId: pos.refId,
      key,
    };

    this.positions[key] = newPosition;

    return newPosition;
  };

  @action deletePosition = (key: string) => {
    this.positions[key] = { ...this.positions[key], _destroy: true };
  };

  @action setUploads = (uploads: UploadedFile[]) => {
    this.uploads = uploads;
  };

  @action reset = () => {
    this.uploads = [];
    this.invoice = undefined;
    this.positions = {};
    this.currentPosition = 0;
    this.form = undefined;
    this.newXometryCreditNote = false;
    this.newXometryCreditNoteCorrection = false;
    this.providerAcceptSelfBilling = undefined;
  };

  @action destroyFile = (id: number) => {
    if (!this.invoice) {
      return;
    }

    const file = this.invoice.attachedFiles.find((item) => item.id === id);

    if (file) {
      file._destroy = true;
    }
  };

  @action create = (values: IncomingInvoiceFormValues) => {
    this.rootStore.pageLoaderStore.enable('Creating Invoice');

    const formData = prepareFormData({
      attachedFiles: [],
      dealId: this.rootStore.dealStore.data.id,
      positions: this.positions,
      publish: false,
      uploads: this.uploads,
      values: { ...values, selfBilling: this.isXometryCreditNote },
    });

    omsApi
      .post<{ id: number }>(routesApi.createIncomingInvoicePath(), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => {
        Notification.Success('Invoice has been created');

        window.location.href = routes.editDealIncomingInvoicePath(this.rootStore.dealStore.data.id, response.data.id);
      })
      .catch((error: AxiosResponseError) => {
        Notification.Error(
          <>
            <div>Failed to create Invoice</div>
            <br />
            {map(error.response?.data.errors?.source, (value, key) => (
              <div key={key}>{`${key}: ${value}`}</div>
            ))}
          </>,
        );
      })
      .finally(() => {
        this.rootStore.pageLoaderStore.disable();
      });
  };

  @action update = (values: IncomingInvoiceFormValues, publish = false) => {
    if (!this.invoice) {
      return;
    }

    this.rootStore.pageLoaderStore.enable(publish ? 'Publishing Invoice' : 'Updating Invoice');

    const formData = prepareFormData({
      attachedFiles: this.invoice.attachedFiles,
      dealId: this.rootStore.dealStore.data.id,
      positions: this.positions,
      publish,
      uploads: this.uploads,
      values: { ...values, selfBilling: this.isXometryCreditNote },
      invoiceState: this.invoice.state as IncomingInvoiceState,
    });

    omsApi
      .patch(routesApi.incomingInvoicePath(this.invoice.id), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => {
        if (this.invoice) {
          window.location.href = `${routes.editDealIncomingInvoicePath(
            this.rootStore.dealStore.data.id,
            this.invoice.id,
          )}?publish=${publish.toString()}`;
        }
      })
      .catch((error: AxiosResponseError) => {
        Notification.Error(
          <>
            <div>Failed to update Invoice</div>
            <br />
            {map(error.response?.data.errors?.source, (value, key) => (
              <div key={key}>{`${key}: ${value}`}</div>
            ))}
          </>,
        );
      })
      .finally(() => {
        this.rootStore.pageLoaderStore.disable();
      });
  };

  @action updatePositionAndCloseEdit = (key: string, position: Position): void => {
    this.positions[key] = { ...this.positions[key], ...position };

    if (II_POSITIONS_NEED_CURRENCY) {
      this.positions[key].priceMoney = {
        ...XMoney(this.positions[key].price).toIMoney(),
        currencyCode: this.positions[key].priceMoney?.currencyCode || '',
      };
    }
  };

  @action changeState = (state: IncomingInvoiceState) => {
    if (!this.invoice) {
      return;
    }

    this.rootStore.pageLoaderStore.enable(`Incoming Invoice #${this.invoice.id} is being changed to "${state}" state`);

    omsApi
      .patch(routesApi.changeStateIncomingInvoicePath(this.invoice.id), {
        state,
      })
      .then(() => {
        Notification.Success(`Invoice has been changed to "${state}"`);
        window.location.reload();
      })
      .catch(() => Notification.Error('Failed to change state for Invoice'))
      .finally(() => {
        this.rootStore.pageLoaderStore.disable();
      });
  };

  @action manualChangeState = (state: IncomingInvoiceState) => {
    if (this.invoice) {
      this.invoice = { ...this.invoice, state };
    }
  };

  @action setIsNewXometryCN = (value: boolean) => {
    this.newXometryCreditNote = value;
  };

  @action setIsNewXometryCNCorrection = (value: boolean) => {
    this.newXometryCreditNoteCorrection = value;
  };

  @action setProviderAcceptXometryCreditNote = (value: boolean | undefined) => {
    this.providerAcceptSelfBilling = value;
  };

  @action setRefInvoicesCurrency = (value: CurrencyEnum) => {
    this.refInvoiceCurrency = value;
  };

  @computed
  get hasUnsavedUploads(): boolean {
    return this.uploads.length > 0;
  }

  @computed
  get warningPartnerNotAcceptCreditNote() {
    if (!this.isXometryCreditNote) return false;

    const { providerAcceptSelfBilling } = this;

    if (providerAcceptSelfBilling === undefined) return false;

    return !providerAcceptSelfBilling;
  }

  @computed
  get warningUseXometryCreditNote() {
    if (this.isXometryCreditNote) return false;

    const { providerAcceptSelfBilling } = this;

    if (providerAcceptSelfBilling === undefined) return false;

    return providerAcceptSelfBilling;
  }

  @computed({ keepAlive: true })
  get positionsArray() {
    const positionsArrayDefault = Object.values(this.positions);

    return XOMETRY_VAT_ENABLED
      ? positionsArrayDefault.map((position) => ({
          ...position,
          vat: this.vatInfo?.vatRate != null ? this.vatInfo.vatRate : position.vat || DEFAULT_VAT,
        }))
      : positionsArrayDefault;
  }

  @computed({ keepAlive: true })
  get positionsByShipping() {
    const positionsArrayDefault = Object.values(this.positions);

    const positionsArrayWithVat = positionsArrayDefault.map((position) => ({
      ...position,
      vat: this.vatInfo?.vatRate != null ? this.vatInfo?.vatRate : position.vat,
    }));

    const positions = XOMETRY_VAT_ENABLED ? positionsArrayWithVat : positionsArrayDefault;

    return groupBy(positions, 'refSource');
  }

  @computed
  get isXometryCreditNote() {
    return Boolean(this.newXometryCreditNote || this.invoice?.selfBilling);
  }

  @computed
  get invoiceName() {
    return getInvoiceShortName(this.invoice);
  }

  @computed get pageTitleComputed() {
    return getInvoiceFullName({
      isNew: this.invoice?.id == undefined,
      isCreditNote: this.invoice?.creditNote || this.isCreditNote,
      selfBilling: this.isXometryCreditNote,
      shortName: this.invoiceName,
    });
  }

  @computed({ keepAlive: true })
  get initialValues(): IncomingInvoiceFormValues {
    if (!this.invoice) {
      const query = new URLSearchParams(window.location.search);
      const refId = query.get('ref_id');

      const defaultCleanValues = {
        creditNote: !!refId || this.newXometryCreditNoteCorrection,
        selfBilling: this.isXometryCreditNote,
        refInvoiceId: refId ? Number(refId) : undefined,
        isStorno: false,
        isPartial: false,
        isPublishedInPa: false,
        issuedAt: dayjs(),
        providerBankDetails: {
          bankCountry: null,
          bankAccountHolderName: null,
          iban: null,
          swiftbic: null,
          name: null,
        },
        providerBillingAddress: {
          country: null,
          zip: null,
          city: null,
          address: null,
        },
        providerContact: {
          name: null,
          phone: null,
          email: null,
        },
        positions: {},
        validity: IncomingInvoiceValidityEnum.ManagerReady,
      };

      const defaultCleanValuesEU = {
        ...defaultCleanValues,
        vatPdfCorrectionRequired: false,
      };

      const defaultCleanValuesConfig = {
        [Regions.EU]: defaultCleanValuesEU,
        [Regions.TR]: defaultCleanValues,
        [Regions.UK]: defaultCleanValues,
        [Regions.SG]: defaultCleanValues,
      };

      return defaultCleanValuesConfig[CURRENT_REGION];
    }

    const defaultValues = {
      number: this.invoice.number ?? undefined,
      comment: this.invoice.comment ?? undefined,
      datevComment: this.invoice.datevComment ?? undefined,
      intacctComment: this.invoice.intacctComment ?? undefined,
      needFixComment: this.invoice.needFixComment ?? undefined,
      selfBilling: this.isXometryCreditNote,
      creditNote: this.invoice.creditNote,
      isStorno: Boolean(this.invoice.isStorno),
      isPartial: this.invoice.isPartial,
      refInvoiceId: this.invoice.refInvoiceId != null ? Number(this.invoice.refInvoiceId) : undefined,
      dealProviderOrderId:
        this.invoice.dealProviderOrderId != null ? Number(this.invoice.dealProviderOrderId) : undefined,
      isPublishedInPa: Boolean(this.invoice.isPublishedInPa),
      issuedAt: this.invoice.issuedAt ? parseDate(this.invoice.issuedAt) : dayjs(),
      paymentDueDate: this.invoice.paymentDueDate ? parseDate(this.invoice.paymentDueDate) : null,
      registeredAt: this.invoice.registeredAt ? parseDate(this.invoice.registeredAt) : null,
      paidAt: this.invoice.paidAt ? parseDate(this.invoice.paidAt) : null,
      holdUntil: this.invoice.holdUntil ? parseDate(this.invoice.holdUntil) : null,
      providerBankDetails: normalizeProviderBankDetails(this.invoice.providerBankDetails),
      providerBillingAddress: normalizeProviderBillingAddress(this.invoice.providerBillingAddress),
      provider: this.invoice.provider
        ? {
            datevKonto: this.invoice.provider.datevKonto ?? undefined,
            id: Number(this.invoice.provider.id),
            intacctId: this.invoice.provider.intacctId ?? undefined,
            legalName: this.invoice.provider.legalName ?? undefined,
            ...(PROVIDER_NOT_VAT_PAYER_ENABLED && {
              notVatPayer: this.invoice.provider.notVatPayer ?? undefined,
            }),
            payoutTransferType: this.invoice.provider.payoutTransferType ?? undefined,
            vatId: this.invoice.provider.vatId ?? undefined,
          }
        : undefined,
      providerContact: normalizeProviderContact(this.invoice.providerContact),
      positions: this.positions,
      validity: this.invoice.validity ?? IncomingInvoiceValidityEnum.ManagerReady,
    };

    const defaultValuesEU = {
      ...defaultValues,
      vatPdfCorrectionRequired: this.invoice.vatPdfCorrectionRequired,
      xometryVatNumber: normalizeXometryVatNumberForForm(this.invoice.xometryVatNumber),
      xometryVatNumberId: this.invoice.xometryVatNumber?.id ?? undefined,
      sachkontoNumber: this.invoice.sachkontoNumber ?? undefined,
      datevVatCode: this.invoice.datevVatCode ?? undefined,
    };

    const defaultValuesConfig = {
      [Regions.EU]: defaultValuesEU,
      [Regions.TR]: defaultValues,
      [Regions.UK]: defaultValues,
      [Regions.SG]: defaultValues,
    };

    return defaultValuesConfig[CURRENT_REGION];
  }

  @computed({ keepAlive: true })
  get positionsSummary() {
    const summary = {
      netto: XMoney(0),
      brutto: XMoney(0),
      vatAvg: 0,
      positions: 0,
      items: 0,
    };

    const positionsArray = this.positionsArray.filter((position) => !position._destroy);

    let vatAmount = XMoney(0);

    const isSameVat = size(uniq(map(positionsArray, (position) => Number(Number(position.vat).toFixed(2))))) === 1;

    positionsArray.forEach((position) => {
      const newVatAmount = XMoney(vatAmount.toIMoney().amount, position.priceMoney?.currencyCode);
      const newNetto = XMoney(summary.netto.toIMoney().amount, position.priceMoney?.currencyCode);
      const defaultPositionPrice = position.priceMoney || position.price;
      const totalPositionPrice = XMoney(defaultPositionPrice).mult(position.quantity);

      summary.netto = newNetto.plus(totalPositionPrice);
      vatAmount = newVatAmount.plus(totalPositionPrice.mult(position.vat || 0).divide(100));

      summary.positions += 1;
      summary.items += position.quantity;
    });

    const nettoAmount = Number(summary.netto.toIMoney().amount ?? 0);
    summary.vatAvg = nettoAmount !== 0 ? (Number(vatAmount.toIMoney().amount ?? 0) / nettoAmount) * 100 : 0;
    summary.vatAvg = Number(summary.vatAvg.toFixed(2));

    if (XOMETRY_VAT_ENABLED) {
      summary.vatAvg = this.vatInfo?.vatRate || summary.vatAvg || 0;
    }

    if (isSameVat) {
      vatAmount = summary.netto.mult(summary.vatAvg).divide(100);
    }

    summary.brutto = summary.netto.plus(vatAmount);

    return summary;
  }

  @computed({ keepAlive: true })
  get isInputDisabled() {
    return [IncomingInvoiceState.Published, IncomingInvoiceState.Canceled, IncomingInvoiceState.SentToIntacct].includes(
      this.invoice?.state as IncomingInvoiceState,
    );
  }
}

export default IncomingInvoicesStore;
